<template>
  <div style="overflow-x:hidden;">

    <!-- ═══ PAGE HERO ═══ -->
    <div class="nf-page-hero">
      <h1>About Us</h1>
      <ul class="nf-breadcrumb">
        <li><router-link to="/">Home</router-link></li>
        <li class="sep"><i class="fas fa-chevron-right" style="font-size:.65rem;"></i></li>
        <li class="active">About Us</li>
      </ul>
    </div>

    <!-- ═══ WHO WE ARE ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-lines">
      <div class="container-fluid">
        <div class="row g-5 align-items-center">
          <div class="col-12 col-lg-5 nf-reveal-left">
            <div class="nf-img-stack">
              <img :src="'/img/nexus logo.jpg'" class="nf-img-back img-fluid rounded-4 shadow-lg" alt="About Nexus" />
              <img :src="'/img/carousel-1.jpeg'" class="nf-img-front img-fluid rounded-4 shadow-lg" alt="Our Work" />
              <div class="nf-years-badge">
                <span class="nf-years-num">7+</span>
                <span class="nf-years-label">Years of<br>Impact</span>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-7 nf-reveal-right">
            <span class="section-label">Our Story</span>
            <h2 class="section-title">Who We Are</h2>
            <hr class="section-divider left mb-4" />
            <p class="mb-3">
              At Nexus Focus Consultancy, we are driven by the understanding that people, nature, culture, and governance are deeply interconnected.
            </p>
            <p class="mb-3">
              Founded on the belief that sustainable development must honour both tradition and innovation, we work to harmonize environmental conservation with inclusive progress.
            </p>
            <p class="mb-3">
              As a dynamic Kenyan consultancy, we sit at the intersection of research, policy, education, and grassroots action. We empower communities, support institutions, and collaborate with partners to co-create solutions that strengthen climate resilience, reduce disaster risks, and promote environmental justice.
            </p>
            <p class="mb-4">
              By blending indigenous knowledge with global expertise, we catalyze people-centered systems that are resilient, equitable, and sustainable.
            </p>
            <router-link to="/quote" class="btn btn-primary px-5 py-2 rounded-pill">Get a Quote</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ MISSION & VISION ═══ -->
    <section class="py-5 px-3 px-lg-5 nf-section-dots">
      <div class="container-fluid">
        <div class="row g-4">
          <div class="col-12 col-md-6 nf-reveal-left">
            <div class="nf-mv-card h-100">
              <div class="nf-mv-icon"><i class="fas fa-bullseye"></i></div>
              <h3 class="h4 mb-3">Mission Statement</h3>
              <p class="mb-0">To drive inclusive, evidence-based solutions that strengthen climate resilience, environmental sustainability, and equitable development.</p>
            </div>
          </div>
          <div class="col-12 col-md-6 nf-reveal-right">
            <div class="nf-mv-card nf-mv-card--gold h-100">
              <div class="nf-mv-icon nf-mv-icon--gold"><i class="fas fa-eye"></i></div>
              <h3 class="h4 mb-3">Vision Statement</h3>
              <p class="mb-0">A sustainable, just world where climate action and social equity shape resilient communities and thriving ecosystems.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ LEGAL STATUS ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-lines">
      <div class="container-fluid">
        <div class="text-center mb-5 nf-reveal">
          <span class="section-label">Company Details</span>
          <h2 class="section-title">Legal Status &amp; Ownership</h2>
          <hr class="section-divider" />
        </div>
        <div class="row g-4 justify-content-center nf-stagger">
          <div class="col-12 col-sm-6 col-lg-4" v-for="(item, i) in legalItems" :key="i">
            <div class="nf-card text-center">
              <div class="nf-icon-wrap mx-auto"><i :class="item.icon"></i></div>
              <h5 class="mb-1">{{ item.title }}</h5>
              <p class="text-muted mb-0">{{ item.value }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ FOCUS AREAS ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-dots">
      <div class="container-fluid">
        <div class="text-center mb-5 nf-reveal">
          <span class="section-label">What We Focus On</span>
          <h2 class="section-title">Our Focus Areas</h2>
          <hr class="section-divider" />
          <p class="text-muted mx-auto" style="max-width:600px;">
            We specialize in delivering solutions across critical development and sustainability areas.
          </p>
        </div>
        <div class="row g-4 nf-stagger">
          <div class="col-12 col-sm-6 col-lg-4" v-for="(area, i) in focusAreas" :key="i">
            <div class="nf-card">
              <div class="nf-icon-wrap"><i :class="area.icon"></i></div>
              <h5 class="mb-2">{{ area.title }}</h5>
              <p class="small text-muted mb-0">{{ area.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ CORE VALUES ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-lines">
      <div class="container-fluid">
        <div class="text-center mb-5 nf-reveal">
          <span class="section-label">Our Principles</span>
          <h2 class="section-title">Core Values</h2>
          <hr class="section-divider" />
          <p class="text-muted mx-auto" style="max-width:600px;">
            We are guided by principles that reflect our commitment to integrity, inclusion, and innovation.
          </p>
        </div>
        <div class="row g-4 nf-stagger">
          <div class="col-12 col-sm-6 col-lg-4" v-for="(val, i) in coreValues" :key="i">
            <div class="nf-card">
              <div class="nf-icon-wrap"><i :class="val.icon"></i></div>
              <h5 class="mb-2">{{ val.title }}</h5>
              <p class="small text-muted mb-0">{{ val.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ TARGET CLIENTS & PARTNERS ═══ -->
    <section class="py-6 px-3 px-lg-5 nf-section-dots">
      <div class="container-fluid">
        <div class="text-center mb-5 nf-reveal">
          <span class="section-label">Who We Work With</span>
          <h2 class="section-title">Target Clients &amp; Partners</h2>
          <hr class="section-divider" />
          <p class="text-muted mx-auto" style="max-width:600px;">
            We collaborate with a diverse set of stakeholders to implement inclusive and sustainable solutions.
          </p>
        </div>
        <div class="row g-4 nf-stagger">
          <div class="col-12 col-sm-6 col-lg-4" v-for="(client, i) in clientsPartners" :key="i">
            <div class="nf-card">
              <div class="nf-icon-wrap"><i :class="client.icon"></i></div>
              <h5 class="mb-2">{{ client.title }}</h5>
              <p class="small text-muted mb-0">{{ client.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ CTA ═══ -->
    <section class="nf-cta-band py-6 px-3 text-center">
      <div class="container" style="max-width:600px; position:relative; z-index:1;">
        <span class="section-label" style="color:rgba(255,255,255,0.7);">Work With Us</span>
        <h2 class="text-white mt-1 mb-3" style="font-size:clamp(1.8rem,3vw,2.5rem);">
          Ready to Make an Impact Together?
        </h2>
        <div class="d-flex flex-wrap justify-content-center gap-3 mt-4">
          <router-link to="/quote"   class="btn btn-primary   btn-lg rounded-pill px-5">Request a Service</router-link>
          <router-link to="/contact" class="btn btn-secondary btn-lg rounded-pill px-5">Contact Us</router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'AboutUs',
  data() {
    return {
      legalItems: [
        { icon: 'fas fa-gavel',         title: 'Legal Form',                 value: 'Private Limited Company' },
        { icon: 'fas fa-map-marker-alt',title: 'Country of Incorporation',   value: 'Kenya'                   },
        { icon: 'fas fa-id-card',        title: 'Registration Number',        value: 'PVT-Q7U96X69'            },
      ],
      focusAreas: [
        { icon: 'fa fa-globe',      title: 'Climate Resilience & Disaster Risk Reduction',   description: 'Strengthening capacities to withstand climate shocks and build adaptive communities.' },
        { icon: 'fa fa-gavel',      title: 'Environmental Governance & Policy Advocacy',     description: 'Promoting strong environmental institutions, laws, and policy reforms.' },
        { icon: 'fa fa-users',      title: 'Community Empowerment & Social Equity',          description: 'Enhancing participation, equity, and rights for marginalized groups.' },
        { icon: 'fa fa-leaf',       title: 'Sustainable Livelihoods & Natural Resources',   description: 'Supporting resilient income strategies and sustainable ecosystems.' },
        { icon: 'fa fa-lightbulb',  title: 'Knowledge, Innovation & Systems Strengthening', description: 'Leveraging innovation and data systems for effective solutions.' },
        { icon: 'fa fa-handshake',  title: 'SDGs & ESG Integration',                        description: 'Aligning programs with global goals and ESG principles.' },
      ],
      coreValues: [
        { icon: 'fas fa-gavel',     title: 'Integrity',      description: 'Upholding transparency, accountability, and ethical practices in all our work.' },
        { icon: 'fas fa-users',     title: 'Inclusivity',    description: 'Fostering gender equity, youth participation, and community-driven development.' },
        { icon: 'fas fa-lightbulb', title: 'Innovation',     description: 'Leveraging creativity, science, and local knowledge to drive impactful solutions.' },
        { icon: 'fas fa-leaf',      title: 'Sustainability', description: 'Advancing long-term, resilient solutions that protect ecosystems and livelihoods.' },
        { icon: 'fas fa-handshake', title: 'Collaboration',  description: 'Building strong, cross-sector partnerships for meaningful and lasting change.' },
      ],
      clientsPartners: [
        { icon: 'fas fa-building',          title: 'County & National Governments',     description: 'We collaborate with government bodies on sustainable development projects.' },
        { icon: 'fas fa-hand-holding-usd',  title: 'Development Partners & Donors',    description: 'We partner with agencies that fund and support development initiatives globally.' },
        { icon: 'fas fa-users-cog',         title: 'NGOs & Community Organisations',   description: 'We collaborate with local and international NGOs to empower communities.' },
        { icon: 'fas fa-university',        title: 'Research & Academic Institutions', description: 'We partner with academic institutions to conduct research and innovation.' },
        { icon: 'fas fa-industry',          title: 'Private Sector & Farmer Groups',   description: 'We provide solutions for businesses and farmer groups to grow sustainably.' },
        { icon: 'fas fa-users',             title: 'Local Communities',                description: 'We work directly with communities to foster inclusive, sustainable development.' },
      ],
    };
  },
};
</script>

<style scoped>
/* ── Image Stack ── */
.nf-img-stack {
  position: relative;
  padding-bottom: 60px;
  padding-right: 60px;
}
.nf-img-back  { width:100%; object-fit:cover; }
.nf-img-front {
  position:absolute; bottom:0; right:0;
  width:55%;
  border:5px solid #fff;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
}
.nf-years-badge {
  position:absolute; top:24px; right:24px;
  background: var(--nf-gold,#c9992d); color:#fff;
  border-radius:50%; width:90px; height:90px;
  display:flex; flex-direction:column;
  align-items:center; justify-content:center; text-align:center;
  box-shadow:0 6px 20px rgba(201,153,45,0.45); line-height:1.2;
}
.nf-years-num   { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:700; }
.nf-years-label { font-size:0.62rem; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; opacity:.9; }

/* ── Mission / Vision Cards ── */
.nf-mv-card {
  background:#fff;
  border-radius:14px;
  padding:2.5rem 2rem;
  border-left:4px solid var(--nf-green,#1a6b4a);
  box-shadow:0 4px 20px rgba(0,0,0,0.05);
}
.nf-mv-card--gold { border-left-color: var(--nf-gold,#c9992d); }
.nf-mv-icon {
  width:56px; height:56px; border-radius:14px;
  background:var(--nf-green-tint,#e7f4ed);
  display:flex; align-items:center; justify-content:center;
  font-size:1.3rem; color:var(--nf-green,#1a6b4a);
  margin-bottom:1.25rem;
}
.nf-mv-icon--gold { background:var(--nf-gold-light,#fdf4e0); color:var(--nf-gold,#c9992d); }

/* ── CTA ── */
.nf-cta-band {
  background:linear-gradient(135deg,var(--nf-charcoal,#2c3e50) 0%, var(--nf-green-dark,#0e4530) 100%);
  position:relative; overflow:hidden;
}
.nf-cta-band::before {
  content:''; position:absolute; inset:0;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size:40px 40px;
}

@media (max-width:767px) {
  .nf-img-stack { padding-right:36px; padding-bottom:40px; }
  .nf-img-front { width:50%; }
  .nf-years-badge { width:72px; height:72px; }
  .nf-years-num { font-size:1.1rem; }
}
</style>
